import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface ProcessingRule {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  rule_type: 'validation' | 'extraction' | 'classification' | 'anomaly_detection';
  conditions: any;
  actions: any;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateProcessingRuleData {
  name: string;
  description?: string;
  rule_type: 'validation' | 'extraction' | 'classification' | 'anomaly_detection';
  conditions: any;
  actions: any;
  priority?: number;
  is_active?: boolean;
}

export const useProcessingRules = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const rulesQuery = useQuery({
    queryKey: ['processing-rules', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('processing_rules')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as ProcessingRule[];
    },
    enabled: !!user?.id,
  });

  const createRuleMutation = useMutation({
    mutationFn: async (ruleData: CreateProcessingRuleData) => {
      const { data, error } = await supabase
        .from('processing_rules')
        .insert([{
          ...ruleData,
          user_id: user?.id,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processing-rules'] });
      toast({
        title: "Rule created",
        description: "Processing rule has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create rule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateRuleMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateProcessingRuleData> }) => {
      const { data, error } = await supabase
        .from('processing_rules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processing-rules'] });
      toast({
        title: "Rule updated",
        description: "Processing rule has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update rule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('processing_rules')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processing-rules'] });
      toast({
        title: "Rule deleted",
        description: "Processing rule has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete rule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    rules: rulesQuery.data || [],
    isLoading: rulesQuery.isLoading,
    error: rulesQuery.error,
    createRule: createRuleMutation.mutate,
    updateRule: updateRuleMutation.mutate,
    deleteRule: deleteRuleMutation.mutate,
    isCreating: createRuleMutation.isPending,
    isUpdating: updateRuleMutation.isPending,
    isDeleting: deleteRuleMutation.isPending,
  };
};